import * as React from 'react';

import { AgojiBackground } from '@v2/components/Background/AgojiBackground';
import { useAppSetting } from '@contexts/appSetting/hooks/useAppSetting';
import { useIsMobile } from '@contexts/app/hooks/useIsMobile';
import { useIsCitiWhitelabel } from '@contexts/app/hooks/useIsCitiWhitelabel';
import { Box } from '@drone-js/core';
import { PaddingType } from '@drone-js/theme';

export function withBackground<TProps>(Component: React.FC<TProps>): React.FC<React.PropsWithRef<TProps>> {
    const MemoizedComponent = React.memo<React.FC<TProps>>(Component);

    const ComponentWithBackground: React.FC<React.PropsWithRef<TProps>> = props => {
        const { customUI } = useAppSetting();

        const isMobile = useIsMobile();
        const isCiti = useIsCitiWhitelabel();
        // this condition is to have fixed width for Citi on desktop
        const isCitiFixedWidth = isCiti && !isMobile;

        if (!customUI?.background?.showBackground) {
            return <MemoizedComponent {...props} />;
        }

        /*
         * This condition is to show the card in the center of page when there is no header
         */
        const isFixedWidth = customUI?.background?.fixedWidthContent;

        const isSpaView = customUI?.background?.spaView;
        // for more padding between header and card
        const py: PaddingType = !isSpaView ? '0' : isMobile ? '24' : '48';

        if (!customUI?.background?.showBackground) {
            return <MemoizedComponent {...props} />;
        }

        const showBgImage = customUI?.background?.useBackgroundImage;
        const useDimBg = customUI?.background?.useDimBottom;

        return (
            <AgojiBackground showBackgroundImage={showBgImage} useDimBottom={useDimBg}>
                <Box display="flex" alignItems="center" justifyContent="center" py={py}>
                    <Box
                        borderRadius="8"
                        data-cy="with-background-wrapper"
                        width={isCitiFixedWidth ? '700px' : isFixedWidth ? undefined : '100%'}
                        maxWidth={isFixedWidth ? '480px' : '100%'}
                        backgroundColor="base.surface"
                    >
                        <MemoizedComponent {...props} />
                    </Box>
                </Box>
            </AgojiBackground>
        );
    };

    ComponentWithBackground.displayName = `${Component.displayName || 'Component'}WithBackground`;

    return ComponentWithBackground;
}
